import styled from 'styled-components';
import React, { useState } from 'react';
import { Tooltip } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { type SidebarData, type SidebarChildData } from './types';

type SiderbarProps = {
  sidebarData: SidebarData[];
  defaultActivatedId?: string;
  manuallyActivatedId?: string; // for manually activation use
  onChangeActivated: (activatedId: string) => void; // for clicking component use
  barInnerWidth?: string;
};

export const Sidebar: React.FunctionComponent<SiderbarProps> = (
  props: SiderbarProps,
) => {
  const {
    sidebarData,
    defaultActivatedId = '',
    onChangeActivated,
    barInnerWidth,
    manuallyActivatedId,
  } = props;
  const [activatedId, setActivatedId] = useState<string>(
    manuallyActivatedId ?? defaultActivatedId,
  );

  const [expandIdSet, setExpandIdSet] = useState<Set<string>>(new Set([]));

  React.useEffect(() => {
    if (manuallyActivatedId !== undefined) {
      handleExpandParent(manuallyActivatedId);
      setActivatedId(manuallyActivatedId);
    }
  }, [manuallyActivatedId]);

  const handleExpandParent = (id: string): void => {
    for (const parent of sidebarData) {
      if (parent.id === id) {
        // the id is a parent id
        return;
      }
      // Check if the parent has children
      if (parent.children) {
        for (const child of parent.children) {
          if (child.id === id) {
            // child activated
            // expand the parent
            const newExpandIdSet = new Set(expandIdSet);
            newExpandIdSet.add(parent.id);
            setExpandIdSet(newExpandIdSet);
            return;
          }
        }
      }
    }
  };

  const handleBarBoxClick =
    (
      id: string,
      parentId?: string,
      children?: SidebarChildData[],
    ): (() => void) =>
    (): void => {
      let nextActivatedId = activatedId;

      if (parentId !== undefined) {
        // child clicked
        // active this child
        nextActivatedId = id;
        // expand it's parent
        const newExpandIdSet = new Set(expandIdSet);
        newExpandIdSet.add(parentId);
        setExpandIdSet(newExpandIdSet);
      } else {
        // parent clicked
        // if already expand
        if (expandIdSet.has(id)) {
          // close expand and keep activedId unchanged
          const newExpandIdSet = new Set(expandIdSet);
          newExpandIdSet.delete(id);
          setExpandIdSet(newExpandIdSet);
        } else {
          // if not expanded
          // if have children
          if (children && children.length > 0) {
            // expand itself and keep activedId unchanged
            const newExpandIdSet = new Set(expandIdSet);
            newExpandIdSet.add(id);
            setExpandIdSet(newExpandIdSet);
          } else {
            // if no children
            // activate itself
            nextActivatedId = id;
            // close current expand
            // setExpandId('');
          }
        }
      }

      // only trigger outside onChange when activated box changed
      if (nextActivatedId !== activatedId) {
        setActivatedId(nextActivatedId);
        onChangeActivated(nextActivatedId);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

  const checkIsActivated = (
    id: string,
    children?: SidebarChildData[],
  ): boolean => {
    const isSelfActivated = activatedId === id;
    let isChildActivated = false;

    if (children) {
      children.every(({ id: childId }) => {
        if (childId === activatedId) {
          isChildActivated = true;
          // stop loop if find activited child
          return false;
        }
        return true;
      });
    }

    return isSelfActivated || isChildActivated;
  };

  return (
    <Container>
      {sidebarData.map(
        (
          { id: parentId, children, icon, name, disabled = false, hoverText },
          outerindex: number,
        ) => {
          const isActivated = checkIsActivated(parentId, children);
          const isExpand = expandIdSet.has(parentId);
          const handleClick = disabled
            ? undefined
            : handleBarBoxClick(parentId, undefined, children);

          // disabled color #ABABAB
          const fatherColor = disabled
            ? '#ABABAB'
            : isActivated
              ? '#951D1D'
              : '#101010';

          const expandIcon = isExpand ? <ExpandLess /> : <ExpandMore />;
          return (
            <Tooltip
              title={disabled && hoverText !== undefined ? hoverText : ''}
              key={outerindex}
              placement="right-end"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [50, -80],
                      },
                    },
                  ],
                },
              }}
            >
              <div>
                <BarBox
                  $activated={isActivated && !children}
                  onClick={handleClick}
                  $width={barInnerWidth}
                  $disabled={disabled}
                  style={{ color: fatherColor }}
                >
                  <span className="icon-wrap">{icon}</span>
                  {name}
                  {children && (
                    <span className="expand-drop">{expandIcon}</span>
                  )}
                </BarBox>
                {children &&
                  isExpand &&
                  children.map(
                    (
                      { id: childId, name, disabled = false, hoverText },
                      innerindex: number,
                    ) => {
                      const isActivated = checkIsActivated(childId);
                      return (
                        <Tooltip
                          title={
                            disabled && hoverText !== undefined ? hoverText : ''
                          }
                          key={innerindex}
                          placement="right-end"
                          slotProps={{
                            popper: {
                              modifiers: [
                                {
                                  name: 'offset',
                                  options: {
                                    offset: [50, -80],
                                  },
                                },
                              ],
                            },
                          }}
                        >
                          <BarBox
                            $activated={isActivated}
                            $isChild={true}
                            $width={barInnerWidth}
                            $disabled={disabled}
                            onClick={
                              disabled
                                ? undefined
                                : handleBarBoxClick(childId, parentId)
                            }
                            style={{
                              fontSize: '0.8rem',
                              color: disabled
                                ? '#ABABAB'
                                : isActivated
                                  ? '#951D1D'
                                  : '#101010',
                            }}
                          >
                            {name}
                          </BarBox>
                        </Tooltip>
                      );
                    },
                  )}
              </div>
            </Tooltip>
          );
        },
      )}
    </Container>
  );
};

const Container = styled.div`
  font-size: 14px;
  top: 8rem;
  left: 0;
  display: flex;
  flex-direction: column;
  user-select: none;
  flex-wrap: warp;
  height: auto !important;
`;

const BarBox = styled.div<{
  $activated?: boolean;
  $isChild?: boolean;
  $width?: string;
  $disabled?: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;

  color: #000;
  cursor: ${props => (props?.$disabled ? 'auto' : 'pointer')};
  background-color: ${props =>
    props?.$isChild && !props?.$activated ? '#EFEFEF' : '#fff'};
  color: ${props => (props?.$activated ? '#951D1D' : '#000')};
  font-weight: ${props => (props?.$activated ? 'bold' : 'normal')};
  border-right: ${props => (props?.$activated ? '6px solid #951D1D' : '')};
  width: ${props =>
    props?.$width === undefined ? '18.6rem' : `${props?.$width}`};
  height: 2.5rem;
  padding-left: ${props => (props?.$isChild ? '3.5rem' : '')};

  .expand-drop {
    margin-left: auto;
    padding-right: 1rem;
  }

  .icon-wrap {
    margin: 0 0.55rem 0 1rem;
  }
`;
